import React, { useEffect, useLayoutEffect, useMemo, useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import { _Api } from "../api/_call";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { formatDate } from "../common/helpers/formatDate";

const PRODUCT_MENU_CACHE_KEY = "product_menu_tab_meta";

const getCachedProductMeta = (productId) => {
  if (!productId) return null;
  try {
    const raw = sessionStorage.getItem(`${PRODUCT_MENU_CACHE_KEY}_${productId}`);
    return raw ? JSON.parse(raw) : null;
  } catch (err) {
    return null;
  }
};

const setCachedProductMeta = (meta) => {
  if (!meta?.product_id) return;
  try {
    sessionStorage.setItem(
      `${PRODUCT_MENU_CACHE_KEY}_${meta.product_id}`,
      JSON.stringify(meta)
    );
  } catch (err) {
    // ignore storage errors
  }
};

const resetProductPageScroll = () => {
  if (typeof window.scrollTo === "function") {
    try {
      window.scrollTo({ top: 0, left: 0, behavior: "instant" });
    } catch (err) {
      window.scrollTo(0, 0);
    }
  }

  document.documentElement.scrollTop = 0;
  document.body.scrollTop = 0;

  [
    ".main-content-wrapper-right",
    ".main-content-wrapper",
    ".main-body-main-table-wrap",
    ".product-detail-scroll",
    ".main-body.product-page-wrap",
    ".main-body",
  ].forEach((selector) => {
    document.querySelectorAll(selector).forEach((el) => {
      if (!(el instanceof HTMLElement)) return;
      const previousBehavior = el.style.scrollBehavior;
      el.style.scrollBehavior = "auto";
      el.scrollTop = 0;
      el.scrollLeft = 0;
      el.style.scrollBehavior = previousBehavior;
    });
  });
};

const ProductMenu = (props) => {
  const { t } = useTranslation();
  const location = useLocation();
  const { productId_url: routeProductId } = useParams();
  let {
    product_value_arr,
    setected_opt,
    selectedButton_,
    setSelectedButton_,
    isSaving_,
    setIs_disable,
    productId_url,
    currentPage,
    formId,
  } = props;
  const generalsettings = useSelector((state) => state.app.generalsettings);
  const base_assets = process.env.REACT_APP_BASE_ASSETS_URL;

  const productIdFromRoute = productId_url || routeProductId || "";
  const stateProductArr = location.state?.product_value_arr;
  const cachedMeta = useMemo(
    () => getCachedProductMeta(productIdFromRoute),
    [productIdFromRoute]
  );

  // Prefer live props, then navigation state, then session cache so tabs don't blink while API loads
  const menuProduct = useMemo(() => {
    const fromProps = Array.isArray(product_value_arr) ? product_value_arr[0] : null;
    const fromState = Array.isArray(stateProductArr) ? stateProductArr[0] : null;

    if (fromProps?.product_id) return fromProps;
    if (fromState?.product_id) return fromState;
    if (cachedMeta?.product_id) return cachedMeta;

    return {
      product_id: productIdFromRoute || "",
      name: fromProps?.name || fromState?.name || "",
      varient_type: fromProps?.varient_type || fromState?.varient_type || "",
      sku: fromProps?.sku || fromState?.sku || "",
    };
  }, [product_value_arr, stateProductArr, cachedMeta, productIdFromRoute]);

  const product_id = menuProduct.product_id || productIdFromRoute || "";
  const varientType = String(menuProduct.varient_type || "");

  const advance_to = product_id
    ? `/app/product/productAdvanced/${product_id}`
    : "#";

  // Keep tabs visible when we already know the product id (from URL/state/cache)
  const hasProductContext = Boolean(product_id);
  const AdvanceTabOn = hasProductContext ? true : "row-inactive ";
  const varientShow =
    varientType === "1" || varientType === "2"
      ? true
      : hasProductContext && !varientType
        ? true // keep visible while type is still loading to avoid blink
        : "row-inactive d-none";
  // Only show Choice when we know type is 2 (don't flash it for other types)
  const allShow = varientType === "2" ? true : "row-inactive d-none";

  const linkStateProductArr = useMemo(() => {
    const arr = Array.isArray(product_value_arr) && product_value_arr[0]?.product_id
      ? product_value_arr
      : Array.isArray(stateProductArr) && stateProductArr[0]?.product_id
        ? stateProductArr
        : [
            {
              product_id,
              name: menuProduct.name || "",
              varient_type: menuProduct.varient_type || "",
              sku: menuProduct.sku || "",
            },
          ];
    return arr;
  }, [product_value_arr, stateProductArr, product_id, menuProduct]);

  const [selectedTopNavProduct] = useState(setected_opt);
  const [selectedButton, setButton] = useState(selectedButton_);
  const [logs_data, setLogs_data] = useState([]);

  useLayoutEffect(() => {
    resetProductPageScroll();
  }, [location.pathname]);

  useEffect(() => {
    if (menuProduct?.product_id) {
      setCachedProductMeta({
        product_id: menuProduct.product_id,
        name: menuProduct.name || "",
        varient_type: menuProduct.varient_type || "",
        sku: menuProduct.sku || "",
      });
    }
  }, [menuProduct]);

  useEffect(() => {
    if (productId_url || routeProductId) {
      setButton("pe-none ct-disable");
    }
  }, [productId_url, routeProductId]);

  useEffect(() => {
    if (selectedButton_) {
      setButton(selectedButton_);
    }
  }, [selectedButton_]);

  useEffect(() => {
    setIs_disable(selectedButton);
    // eslint-disable-next-line
  }, [selectedButton]);

  useEffect(() => {
    if (!product_id) return;
    getLogs(product_id);
    // eslint-disable-next-line
  }, [product_id]);

  const getLogs = async (id) => {
    try {
      const postdata = {
        id,
      };
      let resproduct = await _Api(postdata, `api/v1/App/product/getLogs`);
      if (resproduct?.code === 200) {
        setLogs_data(resproduct?.data);
      }
    } catch (err) {
      console.log(err, "err");
    }
  };

  return (
    <React.Fragment>
      <div className="fixed-product-header">
        <h1 className="main-body-heading">
          <Link
            className="main-body-heading text-decoration-none"
            to="/app/product/main"
          >
            {" "}
            {t("product")}
          </Link>
        </h1>
        <div className="main-body-top-tab-bar">
          <div
            className="main-body-top-tab-bar-left-col nav nav-pills"
            role="tablist"
            id="topNavProduct"
          >
            <Link
              className={
                "main-body-top-tab-bar-left-col-a nav-link " +
                selectedTopNavProduct.generalInfo
              }
              state={{ product_value_arr: linkStateProductArr }}
              to={"/app/product/add-new/" + product_id}
            >
              <span className="main-body-top-tab-bar-left-col-a-text">
                {t("General Info")}
              </span>
            </Link>
            <Link
              className={
                "main-body-top-tab-bar-left-col-a nav-link " +
                selectedTopNavProduct.advanced +
                " " +
                AdvanceTabOn
              }
              state={{ product_value_arr: linkStateProductArr }}
              to={advance_to}
            >
              <span className="main-body-top-tab-bar-left-col-a-text">
                {t("Advanced")}
              </span>
            </Link>

            <Link
              state={{ product_value_arr: linkStateProductArr }}
              className={
                "main-body-top-tab-bar-left-col-a nav-link " +
                selectedTopNavProduct.variant +
                " " +
                varientShow
              }
              to={"/app/product/productVariant/" + product_id}
            >
              <span className="main-body-top-tab-bar-left-col-a-text">
                {t("Variant")}
              </span>
            </Link>

            <Link
              state={{ product_value_arr: linkStateProductArr }}
              className={
                generalsettings?.data?.generalsetup?.productChoice === "1"
                  ? "main-body-top-tab-bar-left-col-a nav-link " +
                    selectedTopNavProduct.choice +
                    " " +
                    allShow
                  : "d-none"
              }
              to={"/app/product/productChoice/" + product_id}
            >
              <span className="main-body-top-tab-bar-left-col-a-text">
                {t("Choice")}
              </span>
            </Link>
          </div>
          <div className="main-body-top-tab-bar-right-col tab-bar-save-icon">
            <div className="dropdown product-edit-detail">
              <button
                className="btn btn-secondary dropdown-toggle product-edit-detail-btn"
                type="button"
                id="dropdownMenuButton1"
                data-bs-toggle="dropdown"
                aria-expanded="false"
              >
                <img
                  src={base_assets + "images/icons/note-board-icon-dark.png"}
                  alt=""
                />
              </button>
              <ul
                className="dropdown-menu"
                aria-labelledby="dropdownMenuButton1"
              >
                {logs_data?.map((ele, index) => {
                  return (
                    <li key={index}>
                      <div className="product-edit-detail-single">{ele.msg}</div>
                      <div className="product-edit-detail-single">
                        <span className="product-edit-detail-single-date">
                          {formatDate(ele.date)}
                        </span>
                        <span>
                          <i className="fa fa-user-o" aria-hidden="true"></i>{" "}
                          &nbsp;
                          {ele.name}
                        </span>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </div>
            {selectedButton === "" || selectedButton === "ErrorShowBtn" ? (
              <button
                type="submit"
                form={formId}
                className="main-body-top-tab-bar-right-col-icon"
                disabled={isSaving_}
              >
                <img
                  className="save-icon"
                  src={base_assets + "images/icons/save-icon.png"}
                  alt=""
                />
              </button>
            ) : (
              <img
                className="save-icon"
                onClick={() => {
                  setButton("");
                  // Keep the parent in sync, so its next "read mode" after saving is a real change this menu reacts to.
                  setSelectedButton_?.("");
                }}
                src={base_assets + "images/icons/edit-pencil.png"}
                alt=""
              />
            )}
            <Link
              className="product-edit-detail-single-edit-data"
              to="/app/product/main"
              state={{ currentPage: currentPage ? currentPage : 0 }}
            >
              <button
                type="button"
                className="main-body-top-tab-bar-right-col-icon"
              >
                <img src={base_assets + "images/icons/cross-bg.png"} alt="" />
              </button>
            </Link>
          </div>
        </div>
      </div>
    </React.Fragment>
  );
};

export default ProductMenu;
